/*eslint-disable*/
import React, { useEffect, useState, useRef } from "react";
import FastAPIClient from "../../client";
import config from "../../config";
import ProductDetails from "../../components/LayoutComponents/ProductDetails";
import ProductModal from "../../components/ModalComponents/ProductModal";
import ProductFilterTab from "./ProductFilterTab";
import ProductHeader from "./ProductHeader";
// import Loader from "../../components/Loader";
import "./style.css";

const client = new FastAPIClient(config);

import {
    blankFilters,
    blankExpandedLevels,
} from "../../utils/Products/functions";
import { useNetwork } from "../../components/NetworkProvider";
import { FormattedMessage, IntlProvider } from "react-intl";

import messages_en from "./translations/en.json";
import messages_pt from "./translations/pt.json";
import messages_fr from "./translations/fr.json";

import FiltersMobileModal from "../../components/ModalComponents/FiltersMobileModal";
import ProductSkeleton from "../../components/Skeletons/ProductSkeleton";
import { HiOutlineArrowSmallUp } from "react-icons/hi2";

const messages = {
    en: messages_en,
    pt: messages_pt,
    fr: messages_fr,
};

// Add useMediaQuery hook
const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(false);

    useEffect(() => {
        const media = window.matchMedia(query);
        setMatches(media.matches);

        const handler = (event) => setMatches(event.matches);
        media.addEventListener("change", handler);

        return () => media.removeEventListener("change", handler);
    }, [query]);

    return matches;
};

const Products = () => {
    const limit = 12;
    const { isOnline } = useNetwork();
    const isMdOrLarger = useMediaQuery("(min-width: 768px)");
    const [locale, setLocale] = useState();
    const [shownProducts, setShownProducts] = useState([]);
    const [skip, setSkip] = useState(0);
    const [productCount, setProductCount] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [productLevelOnes, setProductLevelOnes] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState(blankFilters);
    const [mobileFilters, setMobileFilters] = useState(blankFilters);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [subCategories, setSubCategories] = useState(null);
    const [showModal, setShowModal] = useState(false); // Track modal visibility
    const [selectedProduct, setSelectedProduct] = useState(null); // Track the selected product
    const [refreshing, setRefreshing] = useState(true);
    const [batchRefreshing, setBatchRefreshing] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);

    const containerRef = useRef(null);
    const endOfContainerRef = useRef(null);
    const fetchingRef = useRef(false);

    // Initialize isFilterOpen based on screen size
    const [isFilterOpen, setIsFilterOpen] = useState(isMdOrLarger);

    // State for Selection Francois products
    const [selectionFrancoisProducts, setSelectionFrancoisProducts] = useState(
        []
    );

    // Update isFilterOpen when screen size changes
    useEffect(() => {
        setIsFilterOpen(isMdOrLarger);
    }, [isMdOrLarger]);

    // Fetch Selection Francois products on component mount
    useEffect(() => {
        const fetchSelectionProducts = async () => {
            const productIds = ["9671", "9670", "90137", "521930"];
            try {
                const fetchedProducts = await Promise.all(
                    productIds.map((id) => client.getProduct(id))
                );
                setSelectionFrancoisProducts(fetchedProducts);
            } catch (error) {
                console.error(
                    "Error fetching Selection Francois products:",
                    error
                );
            }
        };

        if (isOnline) {
            fetchSelectionProducts();
        }
    }, [isOnline]); // Depend on isOnline to refetch if connectivity changes

    useEffect(() => {
        if (isOnline) {
            const handleScrollEnd = () => {
                if (
                    shownProducts.length > 0 &&
                    shownProducts.length < productCount &&
                    !refreshing &&
                    !batchRefreshing &&
                    !fetchingRef.current
                ) {
                    fetchingRef.current = true;
                    setBatchRefreshing(true);
                    goToNext();
                    setShowScrollTop(true);
                }
            };
            const observer = new IntersectionObserver(
                (entries) => {
                    if (
                        entries[0].isIntersecting &&
                        !batchRefreshing &&
                        shownProducts.length < productCount
                    ) {
                        handleScrollEnd();
                    }
                },
                {
                    root: containerRef.current,
                    threshold: 0.9,
                }
            );

            if (endOfContainerRef.current) {
                observer.observe(endOfContainerRef.current);
            }
            return () => {
                if (endOfContainerRef.current) {
                    observer.unobserve(endOfContainerRef.current);
                }
            };
        }
    }, [shownProducts]);

    const scrollToTop = () => {
        containerRef.current?.scrollTo({
            top: 0,
            behavior: "smooth",
        });
        setShowScrollTop(false);
    };

    useEffect(() => {
        setLocale(localStorage.getItem("language"));
    }, []);
    useEffect(() => {
        if (isOnline) {
            countProducts();
            getProductLevelOnes();
        } else {
            getAllProducts();
        }
    }, [isOnline]);

    const getProducts = async (skip, category, filters) => {
        client
            .getAllProducts({ skip, limit, category }, { filter: filters })
            .then((data) => {
                const list = data?.results;
                if (skip === 0) {
                    setShownProducts(list);
                } else {
                    setShownProducts((prev) => [...prev, ...list]);
                }
            })
            .finally(() => {
                setRefreshing(false);
                setBatchRefreshing(false);
                fetchingRef.current = false; // unlock it
            });
    };
    const getAllProducts = async () => {
        try {
            const data = await client.getAllProductsOffline();
            if (data?.results) {
                setAllProducts(data.results);
                const levels = [];
                data.results.forEach((product) => {
                    if (!levels.includes(product.product_level_1)) {
                        levels.push(product.product_level_1);
                    }
                });
                setProductLevelOnes(levels);

                if (!selectedCategory) {
                    setShownProducts(data.results);
                    setProductCount(data.results.length);
                } else {
                    filterOffline();
                }
            }
        } catch (error) {
            console.error("Error fetching offline products:", error);
        }
    };

    const countProducts = async (category, filters) => {
        client
            .countProducts({ category: category }, { filter: filters })
            .then((data) => {
                setProductCount(data);
            });
    };

    const getProductLevelOnes = async () => {
        client.getProductLevelOnes().then((data) => {
            setProductLevelOnes(data.results);
        });
    };

    const [expandedLevels, setExpandedLevels] = useState(blankExpandedLevels);

    const goToNext = () => {
        getProducts(skip, selectedCategory, selectedFilters);
        setSkip((prev) => prev + limit);
    };

    const filterOffline = () => {
        let results = allProducts;

        if (selectedCategory) {
            // Filter by category if one is selected
            results = results.filter(
                (pr) => pr.product_level_1 === selectedCategory
            );

            // Then apply other filters
            results = results.filter((pr) => {
                for (const [key, selectedValues] of Object.entries(
                    selectedFilters
                )) {
                    if (selectedValues.length === 0) continue;
                    if (!selectedValues.includes(pr[key])) {
                        return false;
                    }
                }
                return true;
            });
        }

        setShownProducts(results);
        setProductCount(results.length);
    };

    useEffect(() => {
        if (isOnline) {
            setRefreshing(true);
            setSkip(0);
            setProductCount(null);
            setShownProducts([]);
            countProducts(selectedCategory, selectedFilters);
            getProducts(0, selectedCategory, selectedFilters);
        } else {
            filterOffline();
        }
    }, [selectedCategory, selectedFilters]);

    return (
        <IntlProvider locale={locale} messages={messages[locale]}>
            {isFilterOpen && (
                <FiltersMobileModal setIsFilterOpen={setIsFilterOpen}>
                    <ProductFilterTab
                        selectedCategory={selectedCategory}
                        setRefreshing={setRefreshing}
                        productLevelOnes={productLevelOnes}
                        setSelectedCategory={setSelectedCategory}
                        expandedLevels={expandedLevels}
                        setExpandedLevels={setExpandedLevels}
                        shownProducts={shownProducts}
                        setShownProducts={setShownProducts}
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                        subCategories={subCategories}
                        setSubCategories={setSubCategories}
                        allProducts={allProducts}
                        mobileFilterOpen={true}
                        mobileFilters={mobileFilters}
                        setMobileFilters={setMobileFilters}
                        setIsFilterOpen={setIsFilterOpen}
                    />
                </FiltersMobileModal>
            )}
            <div
                ref={containerRef}
                className="flex flex-col md:flex-row h-screen overflow-y-auto w-full relative bg-white"
            >
                <div className="w-full md:px-15 md:text-left">
                    <div className="flex flex-col">
                        <ProductHeader
                            {...{
                                selectedCategory,
                                productCount,
                                isFilterOpen,
                                setIsFilterOpen,
                                setSelectedProduct,
                                setShowModal,
                                allProducts,
                                selectedProduct,
                                setSelectedCategory,
                                setSelectedFilters,
                                setExpandedLevels,
                                setSubCategories,
                            }}
                        />
                        <div className="md:m-1 relative justify-between flex pt-2 md:pt-0  ">
                            <div
                                className={`
                                    p-2 md:grid md:p-3 border border-gray-100 transition-all duration-200 ease-in-out text-left rounded-lg overflow-y-auto gray-50 justify-start
                                    ${
                                        isFilterOpen
                                            ? "md:w-4/5"
                                            : "w-full md:w-full"
                                    }
                                    ${
                                        shownProducts.length > 0 ||
                                        refreshing ||
                                        batchRefreshing
                                            ? "grid xl:grid-cols-4 xl:grid-rows-4 grid-rows-2 grid-cols-2"
                                            : ""
                                    }
                                `}
                            >
                                {refreshing && shownProducts.length === 0 ? (
                                    Array.from({ length: limit }).map(
                                        (_, idx) => (
                                            <ProductSkeleton key={idx} />
                                        )
                                    )
                                ) : shownProducts.length > 0 ? (
                                    <>
                                        {(selectedCategory === null ||
                                            selectedCategory === "VINS") &&
                                            selectionFrancoisProducts.map(
                                                (prod) => (
                                                    <ProductDetails
                                                        product={prod}
                                                        key={prod.product_id}
                                                        locale={locale}
                                                        isFilterOpen={
                                                            isFilterOpen
                                                        }
                                                    />
                                                )
                                            )}
                                        {shownProducts.map((prod) => (
                                            <ProductDetails
                                                product={prod}
                                                key={prod.product_id}
                                                locale={locale}
                                                isFilterOpen={isFilterOpen}
                                            />
                                        ))}
                                        {batchRefreshing &&
                                            Array.from({ length: limit }).map(
                                                (_, idx) => (
                                                    <ProductSkeleton
                                                        key={idx}
                                                    />
                                                )
                                            )}
                                    </>
                                ) : (
                                    <div className="mt-4">
                                        <FormattedMessage id="products.no_result" />
                                    </div>
                                )}
                            </div>
                            <div
                                className={`${
                                    isFilterOpen
                                        ? "hidden w-full md:block md:w-1/5"
                                        : "max-w-0"
                                }  delay-150 duration-300 ease-in-out md:max-h-screen h-full transition-all pl-3  relative transition-all `}
                            >
                                <div
                                    className={` h-full w-full bg-white overflow-y-scroll no-scrollbar`}
                                >
                                    <ProductFilterTab
                                        selectedCategory={selectedCategory}
                                        setRefreshing={setRefreshing}
                                        productLevelOnes={productLevelOnes}
                                        setSelectedCategory={
                                            setSelectedCategory
                                        }
                                        expandedLevels={expandedLevels}
                                        setExpandedLevels={setExpandedLevels}
                                        shownProducts={shownProducts}
                                        setShownProducts={setShownProducts}
                                        selectedFilters={selectedFilters}
                                        setSelectedFilters={setSelectedFilters}
                                        subCategories={subCategories}
                                        setSubCategories={setSubCategories}
                                        allProducts={allProducts}
                                        mobileFilterOpen={false}
                                        mobileFilters={mobileFilters}
                                        setMobileFilters={setMobileFilters}
                                        setIsFilterOpen={setIsFilterOpen}
                                    />
                                </div>
                            </div>
                        </div>{" "}
                        <div ref={endOfContainerRef} className="h-32"></div>
                    </div>
                </div>
                {showScrollTop && (
                    <div className="hidden md:fixed bottom-1 right-10 z-10 text-white bg-black  rounded-full">
                        <button
                            onClick={() => scrollToTop()}
                            className="text-lg font-semibold flex justify-center items-center p-2"
                        >
                            <span>
                                <HiOutlineArrowSmallUp />
                            </span>
                        </button>
                    </div>
                )}
                {/* Modal */}
                {showModal && selectedProduct && (
                    <ProductModal
                        showModal={showModal}
                        setShowModal={setShowModal}
                        product={selectedProduct}
                        setProduct={setSelectedProduct}
                    />
                )}
            </div>
        </IntlProvider>
    );
};

export default Products;
